import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Volume2, Clock, Zap, AlertTriangle } from 'lucide-react';

const TTSDecisionModal = ({ isOpen, onYes, onNo, onProceed, onCancel, manuscriptData, manuscript, updatedCharacterCount }) => {
  const [ttsMode, setTtsMode] = useState('full'); // 'dev' or 'full' - Default to 'full' for production
  
  // Support both prop names for backward compatibility
  const data = manuscriptData || manuscript || {};
  const handleYes = onYes || onProceed || (() => console.warn('TTSDecisionModal: No onYes handler provided'));
  const handleNo = onNo || onCancel || (() => console.warn('TTSDecisionModal: No onNo handler provided'));
  
  // Use updated character count if provided (from outline editing), otherwise fall back to original
  const effectiveCharacterCount = updatedCharacterCount || data?.character_count || 0;
  
  // Check for large segments (would need segments array passed in for full check)
  const hasLargeSegments = data.segments?.some(s => s.word_count > 10000);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleNo(); }}>
      <DialogContent className="w-[95vw] max-w-lg sm:w-full">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Volume2 className="h-6 w-6 text-purple-600" />
            Create Audio?
          </DialogTitle>
          <DialogDescription>
            Your manuscript outline is ready. Choose how you want to generate audio:
          </DialogDescription>
        </DialogHeader>
        
        {/* Large Segment Warning */}
        {hasLargeSegments && (
          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  Large Segment Warning
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                  Some segments exceed 10,000 words. Consider splitting them for better TTS processing.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4 py-4">
          {/* TTS Mode Selection */}
          <div className="space-y-3">
            {/* Dev Mode Option - HIDDEN but functionality preserved */}
            {false && (
            <button
              onClick={() => setTtsMode('dev')}
              className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                ttsMode === 'dev'
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-blue-300'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`mt-1 ${ttsMode === 'dev' ? 'text-blue-600' : 'text-gray-400'}`}>
                  <Clock className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Development Mode (Recommended)
                    </p>
                    {ttsMode === 'dev' && (
                      <span className="text-xs px-2 py-0.5 bg-blue-500 text-white rounded-full">
                        Selected
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Generate audio for the <strong>first 75 words</strong> of each chapter. Perfect for testing while minimizing costs.
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 font-medium">
                    💰 Cost: ~${((data?.segment_count || 0) * 75 * 5 * 0.000015).toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    (First 75 words per segment × {data?.segment_count || 0} segments)
                  </p>
                </div>
              </div>
            </button>
            )}

            {/* Full Mode Option */}
            <button
              onClick={() => setTtsMode('full')}
              className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                ttsMode === 'full'
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-purple-300'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`mt-1 ${ttsMode === 'full' ? 'text-purple-600' : 'text-gray-400'}`}>
                  <Zap className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Full Production Mode
                    </p>
                    {ttsMode === 'full' && (
                      <span className="text-xs px-2 py-0.5 bg-purple-500 text-white rounded-full">
                        Selected
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Generate audio for the <strong>entire chapter content</strong>. Use this for final production builds.
                  </p>
                  <p className="text-xs text-purple-600 dark:text-purple-400 mt-1 font-medium">
                    💰 Estimated Cost: ~${(effectiveCharacterCount * 0.000015).toFixed(2)} (full manuscript)
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    ({effectiveCharacterCount.toLocaleString()} characters @ $0.015 per 1K chars)
                  </p>
                </div>
              </div>
            </button>
          </div>

          {/* Manuscript Stats */}
          {data && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Segments:</span>
                <span className="font-medium">{data.segment_count || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Total Words:</span>
                <span className="font-medium">{(data.word_count || 0).toLocaleString()}</span>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <Button
            onClick={handleNo}
            variant="outline"
            className="flex-1"
          >
            Not Now
          </Button>
          <Button
            onClick={() => handleYes(ttsMode)}
            className={`flex-1 ${
              ttsMode === 'dev' 
                ? 'bg-blue-600 hover:bg-blue-700' 
                : 'bg-purple-600 hover:bg-purple-700'
            }`}
          >
            {ttsMode === 'dev' ? 'Generate (Dev Mode)' : 'Generate (Full)'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TTSDecisionModal;
